import { useState } from "react";
import { DropdownButton, Flex, HelpIndicator, Text } from "@jsq/ds/core";
import { Icon } from "@jsq/ds/icons";
import { type EmailInvitationType } from "~/graphql/supergraphTypes";
import { ExtendedInfoTooltip } from "~/pages/Compliance/Common/ExtendedInfoTooltip";
import { ProgramName } from "~/pages/Compliance/utils/constants";
import { formatCountText } from "~/utils/strings";
import InviteInvestorModal from "../../InviteInvestorsModal";
import {
  RemediationActionContainer,
  ActionButton,
  WarningTextColor,
} from "../RemediationRequired/styles_vanilla.css";
import CancelRemediationModal from "./CancelRemediationModal";
import {
  countStyle,
  InviteSentContainer,
  StatusLeftSide,
  tooltiplabelStyle,
  tooltipTableStyle,
} from "./styles_vanilla.css";
import { getRemediationTime, isInvitationOverdue } from "./util";

interface Props {
  positionId: number;
  arenaId: string;
  entityId: string;
  programName: ProgramName;
  positionName: string;
  entityName: string;
  portalStatus: string[] | null;
  complianceInvitation: EmailInvitationType;
}

const RemediationInvite = ({
  positionId,
  arenaId,
  entityId,
  programName,
  positionName,
  entityName,
  portalStatus,
  complianceInvitation,
}: Props) => {
  const [inviteModalOpen, setInviteModalOpen] = useState<boolean>(false);
  const [cancelModalOpen, setCancelModalOpen] = useState<boolean>(false);
  const isPortalEnabled = portalStatus?.includes("VISIBLE") ?? false;

  const dateSent = complianceInvitation.dateSent ? String(complianceInvitation.dateSent) : "--";
  const isOverdue = isInvitationOverdue(dateSent);
  const sentCount = complianceInvitation.sentCount || 0;
  const statusText = isOverdue ? "Request sent (overdue)" : "Request sent";

  const tooltipContent: string | { label: string; value: string }[] = getRemediationTime(
    dateSent,
    sentCount,
  );
  const tooltipRows: { label: string; value: string }[] = Array.isArray(tooltipContent)
    ? tooltipContent
    : [];

  return (
    <>
      <Flex className={isOverdue ? RemediationActionContainer : InviteSentContainer}>
        <Flex className={StatusLeftSide}>
          <Icon type={"envelope-circle-check"} className={isOverdue ? WarningTextColor : ""} />
          <HelpIndicator
            id="arrow"
            placement="top"
            fullWidth
            content={
              tooltipRows.length > 0 ? (
                <table className={tooltipTableStyle}>
                  <tbody>
                    {tooltipRows.map((row, index) => (
                      <tr key={index}>
                        <td className={tooltiplabelStyle}>{row.label}:</td>
                        <td>{row.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                (tooltipContent as string)
              )
            }
          >
            <Text variant="strong-md" className={isOverdue ? WarningTextColor : ""}>
              {statusText}
              {sentCount > 0 && isOverdue && (
                <span className={countStyle}> {formatCountText(sentCount, "time")}</span>
              )}
            </Text>
          </HelpIndicator>
        </Flex>
        <Flex>
          <DropdownButton
            icon="ellipsis-h"
            variant="muted"
            buttonClassName={ActionButton}
            direction="down"
            usePortal
            align="right"
          >
            <DropdownButton.Action
              disabled={!isPortalEnabled}
              onSelect={() => setInviteModalOpen(true)}
            >
              <ExtendedInfoTooltip
                message={
                  !isPortalEnabled
                    ? "Cannot send remediation request. The entity has not been made visible within the portal."
                    : ""
                }
              >
                <Text>Resend remediation request</Text>
              </ExtendedInfoTooltip>
            </DropdownButton.Action>
            <DropdownButton.Action onSelect={() => setCancelModalOpen(true)}>
              Cancel remediation request
            </DropdownButton.Action>
          </DropdownButton>
        </Flex>
      </Flex>
      {inviteModalOpen && (
        <InviteInvestorModal
          positionId={positionId}
          arenaId={arenaId}
          entityId={entityId}
          programName={programName}
          accountName={positionName}
          entityName={entityName}
          dateSent={dateSent}
          sentCount={sentCount}
          onClose={() => setInviteModalOpen(false)}
        />
      )}
      <CancelRemediationModal
        show={cancelModalOpen}
        onHide={() => setCancelModalOpen(false)}
        positionName={positionName}
        entityName={entityName}
        invitationId={complianceInvitation.id}
      />
    </>
  );
};

export default RemediationInvite;
